import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { TripService } from '@/modules/trips'
import { ShareLinksApi } from '@/shared/api/shareLinks'
import { useAuth } from '@/modules/auth'
import { useGuestSession } from '@/modules/auth'
import { TripWithDetailsResponse, type MemberResponse } from '@/shared/api'
import { Badge, Button, Card, CardContent, Skeleton, ContentSkeleton, Input, Logo } from '@/ui/components'
import { PageLayout } from '@/ui/layout'
import { Calendar, Users, Shield, Clock, AlertCircle, Copy, CheckCircle, ExternalLink, Loader2 } from '@/ui/icons'
import { formatDateForDisplay } from '@/modules/trips/utils/dateHelpers'
import { organizeWaypointsByDay } from '@/pages/itinerary-builder/utils/itineraryHelpers'
import { WaypointResponse } from '@/shared/api/routes'
import { getAllWaypointsFromTrip, resolveMeetupDisplay } from '@/modules/trips/utils/meetupWaypointSync'
import { ShareLinkMeta } from '@/components/ShareLinkMeta'
import { getUserProfileUrl } from '@/shared/utils/userImages'

interface AccessContext {
  source: 'invite' | 'private_link' | 'authenticated'
  role: 'guest' | 'viewer' | 'member' | 'owner'
  permissions: {
    viewItinerary: boolean
    viewCampsiteCoordinates: boolean
    viewCrew: boolean
    viewAnnouncements: boolean
    viewRadioChannel?: boolean
    viewMeetupLocation?: boolean
    canEdit: boolean
    canJoin: boolean
  }
  tripId: string
  expiresAt?: string | null
  shareToken?: string
}

interface TripViewPageProps {
  accessContext: AccessContext
}

export function TripViewPage({ accessContext }: TripViewPageProps) {
  const navigate = useNavigate()
  const { getAccessToken, user, isAuthenticated } = useAuth()
  const { setGuestSessionData } = useGuestSession()
  const [trip, setTrip] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'details' | 'crew'>('details')
  const [copiedCoords, setCopiedCoords] = useState<string | null>(null)

  // Join form state (when canJoin)
  const [joinType, setJoinType] = useState<'guest' | 'member'>('guest')
  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [isJoining, setIsJoining] = useState(false)
  const [joinError, setJoinError] = useState('')
  const [joinSuccess, setJoinSuccess] = useState('')

  useEffect(() => {
    const loadTrip = async () => {
      try {
        setIsLoading(true)
        
        let tripData
        
        // If accessing via share link, use the public endpoint with token
        if (accessContext.source === 'private_link' && accessContext.shareToken) {
          const api = new ShareLinksApi(getAccessToken)
          tripData = await api.getTripViaShareLink(accessContext.shareToken)
          setTrip(tripData)
        } else {
          // For authenticated or invite access, use regular endpoint
          const service = new TripService(getAccessToken)
          tripData = await service.getTrip(accessContext.tripId)
          setTrip(tripData)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load trip')
      } finally {
        setIsLoading(false)
      }
    }

    loadTrip()
  }, [accessContext, getAccessToken])

  const handleJoinTrip = useCallback(async () => {
    if (!accessContext.shareToken || !trip) return
    if (isJoining || joinSuccess) return
    if (joinType === 'guest' && (!guestName.trim() || !guestEmail.trim())) {
      setJoinError('Name and email are required to join as a guest')
      return
    }

    setIsJoining(true)
    setJoinError('')
    setJoinSuccess('')

    try {
      const api = new ShareLinksApi(getAccessToken)
      const result = await api.joinTripWithShareToken(accessContext.shareToken, {
        join_type: joinType,
        guest_name: joinType === 'guest' ? guestName.trim() : undefined,
        guest_email: joinType === 'guest' ? guestEmail.trim() : undefined
      })

      if (result?.success) {
        setJoinSuccess(result.message)
        if (joinType === 'guest') {
          setGuestSessionData({
            tripId: result.trip_id || '',
            guestName: guestName.trim(),
            guestEmail: guestEmail.trim(),
            inviteCode: '',
            guestSessionId: (result as { guest_session_id?: string }).guest_session_id || '',
            guestAccessToken: (result as { guest_access_token?: string }).guest_access_token || '',
            isGuest: true
          })
        }
        setTimeout(() => {
          if (joinType === 'guest') {
            navigate(`/trips/${result.trip_id}/guest`, { replace: true })
          } else {
            navigate(`/trips/${result.trip_id}`, { replace: true })
          }
        }, 1500)
      } else {
        setJoinError(result?.message || 'Failed to join trip')
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to join trip'
      setJoinError(msg)
    } finally {
      setIsJoining(false)
    }
  }, [accessContext.shareToken, trip, joinType, guestName, guestEmail, isJoining, joinSuccess, getAccessToken, setGuestSessionData, navigate])

  useEffect(() => {
    if (isAuthenticated && user) {
      setJoinType('member')
      setGuestName(user.user_metadata?.full_name || '')
    }
  }, [isAuthenticated, user])

  // Helper to check if a string is coordinates
  const isCoordinates = (str: string | null | undefined): boolean => {
    if (!str) return false
    const coordPattern = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/
    return coordPattern.test(str.trim())
  }

  // Helper to parse coordinates
  const parseCoordinates = (str: string): { lat: number; lon: number } | null => {
    if (!isCoordinates(str)) return null
    const parts = str.split(',').map(p => p.trim())
    const lat = parseFloat(parts[0])
    const lon = parseFloat(parts[1])
    if (isNaN(lat) || isNaN(lon)) return null
    return { lat, lon }
  }

  // Helper to copy coordinates to clipboard
  const copyCoordinates = async (coords: string) => {
    try {
      await navigator.clipboard.writeText(coords)
      setCopiedCoords(coords)
      setTimeout(() => setCopiedCoords(null), 2000)
    } catch (error) {
    }
  }

  // Helper to get Google Maps URL
  const getGoogleMapsUrl = (coords: string | { lat: number; lon: number }): string => {
    if (typeof coords === 'string') {
      const parsed = parseCoordinates(coords)
      if (!parsed) return '#'
      return `https://www.google.com/maps?q=${parsed.lat},${parsed.lon}`
    }
    return `https://www.google.com/maps?q=${coords.lat},${coords.lon}`
  }

  // Get member display name
  const getMemberDisplayName = (member: any): string => {
    return member.guest_name || member.guest_email || 'Member'
  }

  // Get member initials
  const getMemberInitials = (member: any): string => {
    const name = getMemberDisplayName(member)
    return name
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'M'
  }

  // Get avatar color
  const getAvatarColor = (member: any): string => {
    const colors = [
      'bg-adventure-100 text-adventure-700',
      'bg-earth-100 text-earth-700',
      'bg-adventure-200 text-adventure-800',
      'bg-earth-200 text-earth-800',
      'bg-adventure-300 text-adventure-900',
      'bg-earth-300 text-earth-900'
    ]
    
    const hash = member.id.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  // Extract all waypoints from routeSchedule or trip.waypoints
  // routeSchedule has waypoints organized by day, which is preferred
  // This must be in useMemo and called BEFORE any early returns to ensure hooks are called in consistent order
  const allWaypoints = useMemo((): WaypointResponse[] => {
    const wps = getAllWaypointsFromTrip(trip)
    if (!accessContext.permissions.viewMeetupLocation) {
      return wps.filter((wp) => wp.poi_type !== 'meetup_spot')
    }
    return wps
  }, [trip, accessContext.permissions.viewMeetupLocation])

  const meetupDisplay = useMemo(
    () => (trip ? resolveMeetupDisplay(trip, allWaypoints) : null),
    [trip, allWaypoints]
  )

  const hasWaypoints = allWaypoints.length > 0

  // Organize waypoints by day
  const itineraryDays = useMemo(() => {
    if (!trip) return []
    return organizeWaypointsByDay(allWaypoints, trip.start_date, trip.end_date)
  }, [allWaypoints, trip?.start_date, trip?.end_date])

  const hideNav = accessContext.source === 'private_link'
  if (isLoading) {
    return (
      <PageLayout showHeader={!hideNav}>
        <div className="min-h-screen bg-earth-50">
          <Skeleton className="h-56 w-full rounded-none sm:h-72" />
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="-mt-6 space-y-4">
              <Skeleton className="h-40 w-full rounded-2xl" />
              <ContentSkeleton blocks={2} />
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (error || !trip) {
    return (
      <PageLayout showHeader={!hideNav}>
        <div className="flex min-h-screen items-center justify-center bg-earth-50 px-4 py-10">
          <Card className="w-full max-w-md rounded-2xl shadow-lg">
            <CardContent className="flex flex-col items-center p-6 text-center sm:p-8">
              <Logo size="sm" to="/" className="mb-6" />
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                <AlertCircle className="h-7 w-7 text-destructive" />
              </div>
              <h1 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">
                Failed to load trip
              </h1>
              <p className="text-sm text-muted-foreground">{error}</p>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    )
  }

  // Format day header: "Day 2 Sunday, Feb 1, 2026"
  const formatDayHeader = (day: typeof itineraryDays[0]): string => {
    const dateStr = day.dateRange || day.date
    
    // If dateStr already contains "Day X", use it as is
    if (dateStr.startsWith('Day ')) {
      return dateStr
    }
    
    // Parse the date string (format: "Friday, Nov 29")
    const currentYear = new Date().getFullYear()
    const dateMatch = dateStr.match(/([A-Za-z]+day), ([A-Za-z]+) (\d+)/)
    
    if (dateMatch) {
      const [, dayName, month, dayNum] = dateMatch
      return `Day ${day.day} ${dayName}, ${month} ${dayNum}, ${currentYear}`
    }
    
    // Fallback
    return `Day ${day.day} ${dateStr}`
  }

  return (
    <PageLayout showHeader={!hideNav}>
      {accessContext.source === 'private_link' && accessContext.shareToken && (
        <ShareLinkMeta trip={trip} shareToken={accessContext.shareToken} />
      )}
      <div className="w-full min-h-screen bg-earth-50">
        {/* Cover-photo hero */}
        <div className="relative h-60 w-full overflow-hidden sm:h-80">
          {trip.cover_photo_url ? (
            <img
              src={trip.cover_photo_url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-adventure-500 via-adventure-600 to-adventure-800" />
          )}
          {/* Legibility overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/10" />

          {/* Top row: logo + access pill */}
          <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-4 sm:p-6">
            <div className="rounded-full bg-white/90 px-2.5 py-1.5 shadow-sm backdrop-blur">
              <Logo size="sm" to="/" />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                <Shield className="h-3.5 w-3.5" />
                Shared link
              </span>
              {accessContext.expiresAt && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                  <Clock className="h-3.5 w-3.5" />
                  Expires {new Date(accessContext.expiresAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>

          {/* Bottom: trip identity (extra bottom padding clears the overlapping join card) */}
          <div className="absolute inset-x-0 bottom-0 p-4 pb-10 sm:p-6 sm:pb-16">
            <div className="mx-auto max-w-3xl">
              {trip.status && (
                <Badge className="mb-2 border-0 bg-white/20 text-white capitalize backdrop-blur">
                  {trip.status}
                </Badge>
              )}
              <h1 className="text-2xl font-bold text-white drop-shadow-sm sm:text-4xl break-words">
                {trip.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90">
                {trip.start_date && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    {formatDateForDisplay(trip.start_date)}
                    {trip.end_date && ` – ${formatDateForDisplay(trip.end_date)}`}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-4 w-4 flex-shrink-0" />
                  {trip.members?.length || 0}{' '}
                  {(trip.members?.length || 0) === 1 ? 'crew member' : 'crew'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Join Section - when share link allows joining */}
        {accessContext.source === 'private_link' && accessContext.permissions.canJoin && accessContext.shareToken && (
          <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6">
            <div className="-mt-6 sm:-mt-10">
              <Card className="rounded-2xl border-adventure-200 shadow-lg">
                <CardContent className="p-4 sm:p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Join this trip</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      You can join as a guest or sign in to join as a full member with access to all trip features.
                    </p>
                  </div>

                  {joinSuccess ? (
                    <div className="bg-success/10 border border-success/30 text-success px-4 py-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{joinSuccess}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {joinError && (
                        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>{joinError}</span>
                        </div>
                      )}

                      <div
                        role="tablist"
                        aria-label="Join type"
                        className="grid grid-cols-2 gap-1 rounded-xl bg-earth-100 p-1"
                      >
                        <button
                          type="button"
                          role="tab"
                          onClick={() => setJoinType('guest')}
                          className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 ${
                            joinType === 'guest'
                              ? 'bg-white text-adventure-700 shadow-sm'
                              : 'text-earth-600 hover:text-earth-900'
                          }`}
                          aria-selected={joinType === 'guest'}
                          aria-label="Join as guest"
                        >
                          Guest
                        </button>
                        <button
                          type="button"
                          role="tab"
                          onClick={() => setJoinType('member')}
                          className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 ${
                            joinType === 'member'
                              ? 'bg-white text-adventure-700 shadow-sm'
                              : 'text-earth-600 hover:text-earth-900'
                          }`}
                          aria-selected={joinType === 'member'}
                          aria-label="Join as full member"
                        >
                          Member
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {joinType === 'guest'
                          ? 'Quick access to view this trip — no account needed.'
                          : 'Sign in for full planning access to this trip.'}
                      </p>

                      {joinType === 'guest' ? (
                        <div className="space-y-4 pt-2 animate-in fade-in slide-in-from-top-2">
                          <div className="space-y-2">
                            <label htmlFor="share-join-name" className="text-sm font-medium text-foreground">
                              Your Name
                            </label>
                            <Input
                              id="share-join-name"
                              type="text"
                              value={guestName}
                              onChange={(e) => setGuestName(e.target.value)}
                              placeholder="Enter your name"
                              required
                              autoComplete="name"
                              className="text-base"
                              aria-required="true"
                            />
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="share-join-email" className="text-sm font-medium text-foreground">
                              Your Email
                            </label>
                            <Input
                              id="share-join-email"
                              type="email"
                              value={guestEmail}
                              onChange={(e) => setGuestEmail(e.target.value)}
                              placeholder="Enter your email"
                              required
                              autoComplete="email"
                              className="text-base"
                              aria-required="true"
                            />
                            <p className="text-xs text-muted-foreground">
                              We&apos;ll use this to remember you if you return to this trip
                            </p>
                          </div>
                          <Button
                            onClick={handleJoinTrip}
                            disabled={isJoining || !guestName.trim() || !guestEmail.trim()}
                            className="w-full text-base py-6 gap-2"
                            size="lg"
                            aria-label={isJoining ? 'Joining trip, please wait' : 'Join trip as guest'}
                          >
                            {isJoining ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                                Joining Trip...
                              </>
                            ) : (
                              'Join Trip'
                            )}
                          </Button>
                        </div>
                      ) : isAuthenticated ? (
                        <Button onClick={handleJoinTrip} disabled={isJoining} className="w-full sm:w-auto gap-2">
                          {isJoining ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Joining...
                            </>
                          ) : (
                            'Join as Member'
                          )}
                        </Button>
                      ) : (
                        <div className="bg-adventure-50 border border-adventure-200 text-adventure-800 px-4 py-4 rounded-lg">
                          <p className="text-sm mb-3">
                            Sign in or create an account to join as a full member.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
                              className="gap-2"
                            >
                              Sign in
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => navigate(`/signup?redirect=${encodeURIComponent(window.location.pathname)}`)}
                            >
                              Create account
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Description + tab navigation */}
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          {trip.description && (
            <p className="mt-6 text-sm text-earth-700 sm:text-base break-words">
              {trip.description}
            </p>
          )}
          <div
            role="tablist"
            aria-label="Trip sections"
            className="mt-6 grid grid-cols-2 gap-1 rounded-xl bg-earth-100 p-1 sm:max-w-xs"
          >
            <button
              role="tab"
              aria-selected={activeTab === 'details'}
              onClick={() => setActiveTab('details')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-all touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 ${
                activeTab === 'details'
                  ? 'bg-white text-adventure-700 shadow-sm'
                  : 'text-earth-600 hover:text-earth-900'
              }`}
            >
              Trip Details
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'crew'}
              onClick={() => setActiveTab('crew')}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-all touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 ${
                activeTab === 'crew'
                  ? 'bg-white text-adventure-700 shadow-sm'
                  : 'text-earth-600 hover:text-earth-900'
              }`}
            >
              Crew
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="py-6 sm:py-8">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            {activeTab === 'details' ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Itinerary - Full Width */}
                {accessContext.permissions.viewItinerary && hasWaypoints && (
                  <Card className="rounded-2xl">
                    <CardContent className="p-4 sm:p-6">
                      <div>
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-earth-900 mb-1 sm:mb-2">Itinerary</h3>
                          <p className="text-xs sm:text-sm text-earth-600 mb-3 sm:mb-4">Waypoints and stops along the way</p>
                          
                          {/* Render itinerary by days */}
                          <div className="space-y-6 sm:space-y-8">
                            {itineraryDays.map((day, dayIndex) => {
                              // Calculate starting waypoint number for this day
                              let startWaypointNumber = 1
                              for (let i = 0; i < dayIndex; i++) {
                                startWaypointNumber += itineraryDays[i].waypoints.length
                              }
                              
                              return (
                                <div key={day.day} className="space-y-3 sm:space-y-4">
                                  {/* Day Header */}
                                  <div className="border-b border-earth-200 pb-2 mb-3">
                                    <h4 className="text-sm sm:text-base font-semibold text-earth-900">
                                      {formatDayHeader(day)}
                                    </h4>
                                    {day.waypointCount > 0 && (
                                      <p className="text-xs sm:text-sm text-earth-600 mt-1">
                                        {day.waypointCount} {day.waypointCount === 1 ? 'stop' : 'stops'}
                                      </p>
                                    )}
                                  </div>
                                  
                                  {/* Waypoints for this day */}
                                  <div className="space-y-3 sm:space-y-4">
                                    {day.waypoints.map((waypoint, waypointIndex) => {
                                      // Only show coordinates if permission is granted
                                      const coords = accessContext.permissions.viewCampsiteCoordinates && waypoint.latitude && waypoint.longitude
                                        ? `${waypoint.latitude}, ${waypoint.longitude}`
                                        : null
                                      
                                      const waypointNumber = startWaypointNumber + waypointIndex
                                      
                                      return (
                                        <div key={waypoint.id} className="border-b border-earth-200 last:border-b-0 pb-3 sm:pb-4 last:pb-0">
                                          <div className="flex items-start gap-2 sm:gap-3">
                                            <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-adventure-100 flex items-center justify-center text-adventure-700 font-medium text-xs sm:text-sm">
                                              {waypointNumber}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex flex-wrap items-center gap-2">
                                                <h4 className="text-sm sm:text-base font-semibold text-earth-900 break-words">{waypoint.name || 'Unnamed Waypoint'}</h4>
                                                {accessContext.permissions.viewMeetupLocation &&
                                                  waypoint.poi_type === 'meetup_spot' && (
                                                  <Badge variant="secondary" className="text-[10px] font-medium shrink-0 bg-adventure-100 text-adventure-800 border-0">
                                                    Meetup
                                                  </Badge>
                                                )}
                                              </div>
                                              {waypoint.poi_address && (
                                                <p className="text-xs sm:text-sm text-earth-600 mt-1 break-words">{waypoint.poi_address}</p>
                                              )}
                                              {coords && (
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                                                  <button
                                                    onClick={() => copyCoordinates(coords)}
                                                    className="text-xs sm:text-sm font-medium text-earth-900 hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors touch-manipulation min-h-[44px] sm:min-h-0 -ml-1 pl-1"
                                                    title="Click to copy coordinates"
                                                  >
                                                    <span className="break-all">{coords}</span>
                                                    {copiedCoords === coords ? (
                                                      <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                                                    ) : (
                                                      <Copy className="w-4 h-4 text-earth-400 flex-shrink-0" />
                                                    )}
                                                  </button>
                                                  <a
                                                    href={getGoogleMapsUrl(coords)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-primary hover:text-primary/80 touch-manipulation min-h-[44px] sm:min-h-0 -ml-1 pl-1"
                                                  >
                                                    <ExternalLink className="w-4 h-4 flex-shrink-0" />
                                                    Open in Maps
                                                  </a>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Announcements */}
                {accessContext.permissions.viewAnnouncements && (
                  <Card className="rounded-2xl">
                    <CardContent className="p-4 sm:p-6">
                      <div>
                        <h3 className="text-sm sm:text-base font-semibold text-earth-900 mb-2">Announcements</h3>
                        {trip.announcements && trip.announcements.length > 0 ? (
                          <ul className="space-y-3">
                            {trip.announcements.map((announcement: { id: string; content: string; created_at: string }) => (
                              <li
                                key={announcement.id}
                                className="text-sm text-earth-800 border-b border-earth-100 pb-3 last:border-0 last:pb-0"
                              >
                                <p className="whitespace-pre-wrap break-words">{announcement.content}</p>
                                <time className="text-xs text-earth-500 mt-1 block">
                                  {new Date(announcement.created_at).toLocaleString()}
                                </time>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs sm:text-sm text-earth-500">No announcements yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Radio Channel and Meetup Location - Grid */}
                {(accessContext.permissions.viewRadioChannel || accessContext.permissions.viewMeetupLocation) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {/* Radio Channel */}
                    {accessContext.permissions.viewRadioChannel && (
                      <Card className="rounded-2xl">
                        <CardContent className="p-4 sm:p-6">
                          <div>
                            <h3 className="text-sm sm:text-base font-semibold text-earth-900 mb-2">Radio Channel</h3>
                            {trip.radio_channel || trip.radio_frequency ? (
                              <div className="space-y-1">
                                {trip.radio_channel && (
                                  <div className="text-sm sm:text-base font-semibold text-earth-900">{trip.radio_channel}</div>
                                )}
                                {trip.radio_frequency && (
                                  <div className="text-xs sm:text-sm text-earth-600">{trip.radio_frequency}</div>
                                )}
                                {trip.radio_type && (
                                  <div className="text-xs text-earth-500 capitalize">{trip.radio_type}</div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs sm:text-sm text-earth-500">Not set</div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Meetup Location */}
                    {accessContext.permissions.viewMeetupLocation && (
                      <Card className="rounded-2xl">
                        <CardContent className="p-4 sm:p-6">
                          <div>
                            <h3 className="text-sm sm:text-base font-semibold text-earth-900 mb-2">Meetup Location</h3>
                            {meetupDisplay?.locationLabel ? (
                              <div className="space-y-2">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                  {meetupDisplay.locationIsCoordinates ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          copyCoordinates(
                                            meetupDisplay.coordsForMaps || meetupDisplay.locationLabel!
                                          )
                                        }
                                        className="text-sm sm:text-base font-semibold text-earth-900 hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors touch-manipulation min-h-[44px] sm:min-h-0 -ml-1 pl-1 break-all text-left"
                                        title="Click to copy coordinates"
                                      >
                                        {meetupDisplay.locationLabel}
                                        {copiedCoords === (meetupDisplay.coordsForMaps || meetupDisplay.locationLabel) ? (
                                          <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                                        ) : (
                                          <Copy className="w-4 h-4 text-earth-400 flex-shrink-0" />
                                        )}
                                      </button>
                                      <a
                                        href={getGoogleMapsUrl(
                                          meetupDisplay.coordsForMaps || meetupDisplay.locationLabel
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-primary hover:text-primary/80 touch-manipulation min-h-[44px] sm:min-h-0 -ml-1 pl-1"
                                      >
                                        <ExternalLink className="w-4 h-4 flex-shrink-0" />
                                        Open in Maps
                                      </a>
                                    </>
                                  ) : (
                                    <div className="text-sm sm:text-base font-semibold text-earth-900 break-words">
                                      {meetupDisplay.locationLabel}
                                    </div>
                                  )}
                                </div>
                                {meetupDisplay.coordsForMaps &&
                                  meetupDisplay.locationLabel &&
                                  !meetupDisplay.locationIsCoordinates && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => copyCoordinates(meetupDisplay.coordsForMaps!)}
                                        className="text-xs sm:text-sm font-medium text-earth-700 hover:text-primary cursor-pointer flex items-center gap-1.5 transition-colors text-left break-all"
                                        title="Copy coordinates"
                                      >
                                        {meetupDisplay.coordsForMaps}
                                        {copiedCoords === meetupDisplay.coordsForMaps ? (
                                          <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                                        ) : (
                                          <Copy className="w-4 h-4 text-earth-400 flex-shrink-0" />
                                        )}
                                      </button>
                                      <a
                                        href={getGoogleMapsUrl(meetupDisplay.coordsForMaps)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-primary hover:text-primary/80"
                                      >
                                        <ExternalLink className="w-4 h-4 flex-shrink-0" />
                                        Open pin in Maps
                                      </a>
                                    </div>
                                  )}
                                {meetupDisplay.timeFormatted && (
                                  <div className="text-xs sm:text-sm text-earth-600">{meetupDisplay.timeFormatted}</div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs sm:text-sm text-earth-500">Not set</div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Crew Tab */
              <div>
                <Card className="rounded-2xl">
                  <CardContent className="p-4 sm:p-6">
                    <div className="mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-earth-900 mb-1">Trip Crew</h3>
                      <p className="text-xs sm:text-sm text-earth-600">
                        {trip.members?.length || 0} {trip.members?.length === 1 ? 'member' : 'members'} on this adventure
                      </p>
                    </div>
                    {accessContext.permissions.viewCrew && trip.members && trip.members.length > 0 ? (
                      <div className="space-y-3">
                        {(() => {
                          const members = trip.members as MemberResponse[]
                          const primary = members.filter((m) => !m.parent_member_id)
                          const tagAlongsByParent = members
                            .filter((m) => m.parent_member_id != null)
                            .reduce<Record<string, MemberResponse[]>>((acc, m) => {
                              const pid = m.parent_member_id!
                              if (!acc[pid]) acc[pid] = []
                              acc[pid].push(m)
                              return acc
                            }, {})
                          return primary.map((member) => {
                            const initials = getMemberInitials(member)
                            const displayName = getMemberDisplayName(member)
                            const avatarColor = getAvatarColor(member)
                            const avatarUrl =
                              member.member_type === 'full_member' && member.user_profile?.profile_image_path
                                ? getUserProfileUrl(member.user_profile.profile_image_path)
                                : null
                            const tagAlongs = tagAlongsByParent[member.id] ?? []
                            return (
                              <div key={member.id} className="space-y-1.5">
                                <div className="flex items-center space-x-3">
                                  {avatarUrl ? (
                                    <img
                                      src={avatarUrl}
                                      alt=""
                                      className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-earth-200 dark:border-earth-600"
                                    />
                                  ) : (
                                    <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-semibold text-sm ${avatarColor}`}>
                                      {initials}
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm sm:text-base font-medium text-earth-900 break-words">{displayName}</span>
                                      {member.role && member.role !== 'member' && (
                                        <Badge 
                                          variant="secondary" 
                                          className="text-xs px-1.5 py-0.5 bg-earth-100 text-earth-700 border-0 capitalize flex-shrink-0"
                                        >
                                          {member.role}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {tagAlongs.map((ta) => {
                                  const taInitials = getMemberInitials(ta)
                                  const taName = getMemberDisplayName(ta)
                                  const taColor = getAvatarColor(ta)
                                  const taAvatarUrl =
                                    ta.member_type === 'full_member' && ta.user_profile?.profile_image_path
                                      ? getUserProfileUrl(ta.user_profile.profile_image_path)
                                      : null
                                  return (
                                    <div key={ta.id} className="flex items-center space-x-3 pl-6">
                                      {taAvatarUrl ? (
                                        <img
                                          src={taAvatarUrl}
                                          alt=""
                                          className="flex-shrink-0 w-8 h-8 rounded-full object-cover border border-earth-200 dark:border-earth-600"
                                        />
                                      ) : (
                                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-medium text-xs ${taColor}`}>
                                          {taInitials}
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <span className="text-sm text-earth-700 dark:text-earth-300">{taName}</span>
                                        <span className="text-xs text-earth-500 ml-2">Tag-along</span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )
                          })
                        })()}
                      </div>
                    ) : (
                      <div className="text-xs sm:text-sm text-earth-500">No members</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
